<template>
  <article
    class="accent-card"
    :class="cardClass"
  >
    <TestPixel
      v-for="(testPixel, index) of article.test_pixels"
      :key="index"
      :pixel-link="testPixel"
    />
    <a
      class="accent-card__link"
      :href="removeBurninghutFromUrl(article.url)"
      :target="article.teaser_target === '_blank' ? '_blank' : null"
      :aria-label="`Перейти к статье ${article.title}`"
      @click="onArticleLinkClick"
    />
    <SecretElems
      v-if="isLoggedInWp"
      class="accent-card__secret"
      :article="article"
    />
    <div class="accent-card__image-wrapp">
      <AdaptivePictureAccentCard
        v-if="imageVisible"
        :picture="article.image"
        :size="size"
        :loading="imageLoading"
      />
    </div>
    <div class="accent-card__description">
      <a
        class="accent-card__link"
        :href="removeBurninghutFromUrl(article.url)"
        :target="article.teaser_target === '_blank' ? '_blank' : null"
        :aria-label="`Перейти к статье ${article.title}`"
        @click="onArticleLinkClick"
      />
      <span
        class="accent-card__title"
        v-html="article.title"
      />
      <CardFooter
        class="accent-card__footer"
        :article="article"
        :is-plug="isPlug"
        :hide-comments-count="false"
        :hide-comments="false"
        :hide-date="false"
        :hide-labels="true"
      />
    </div>
  </article>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import { getDateText } from '~/utils/date';
  import CardFooter from '~/components/CardFooter.vue';
  import type { TArticle } from '~/types/common';
  import { removeBurninghutFromUrl } from '~/utils/link';
  import { filterImages } from '~/types/utils/images';
  import type { IMetaArchives } from '~/types/api/archives';
  import AdaptivePictureAccentCard from '~/components/AdaptivePictureAccentCard.vue';
  import SecretElems from '~/components/SecretElems.vue';
  import TestPixel from '~/components/TestPixel.vue';
  import { useUserStore } from '~/features/user/store';
  import { mapState } from 'pinia';

  export default defineNuxtComponent({
    name: 'AccentCard',

    components: {
      SecretElems,
      CardFooter,
      TestPixel,
      AdaptivePictureAccentCard,
    },

    props: {
      article: {
        type: Object as PropType<TArticle>,
        required: true,
      },

      size: {
        type: String as PropType<'big' | 'middle' | 'small'>,
        default: 'small',
      },

      insideTv: {
        type: Boolean as PropType<boolean>,
        default: false,
      },

      meta: {
        type: Object as PropType<IMetaArchives | null>,
        default: null,
      },

      imageLoading: {
        type: String as PropType<'lazy' | 'eager' | 'auto'>,
        default: undefined,
      },
    },

    computed: {
      ...mapState(useUserStore, ['isLoggedInWp']),

      cardClass(): object {
        return {
          'accent-card--without-image': !this.article?.image?.sizes.length && this.size !== 'big',

          [`accent-card--${this.size}`]: this.size,
          'accent-card--big-inside-tv': this.size === 'big' && this.insideTv,
          'accent-card--with-secret': this.isLoggedInWp,
        };
      },

      isPlug() {
        return this.size === 'small' && this.article.image?.sizes.length === 0;
      },

      date(): string {
        return getDateText(this.article.date);
      },

      imageVisible(): boolean {
        return Boolean(this.article.image) || this.size === 'big';
      },

      sizeText() {
        switch (this.size) {
          case 'big':
            return 'большая';
          case 'middle':
            return 'средняя';
          case 'small':
            return 'малая';
          default:
            return 'малая';
        }
      },
    },

    methods: {
      getDateText,
      removeBurninghutFromUrl,
      filterImages,

      onArticleLinkClick(): void {
        this.$sendYandexMetrika({
          level1: `Выбор материала_Виджет ${this.sizeText} карточка`,
          level4: this.article.url,
        });
        this.$emit('clickArticleLink');
      },
    },
  });
</script>

<style lang="scss" scoped>
  .accent-card {
    position: relative;
    border-radius: 32px;
    transform: translateZ(0);
    height: 400px;
    overflow: hidden;
    display: flex;
    align-items: stretch;
    z-index: 1;

    &__secret {
      position: absolute;
      display: flex;
      top: 24px;
      left: 16px;
      z-index: 4;

      > * {
        margin-left: 8px;

        &:first-child {
          margin-left: 0;
        }
      }

      @include tablet {
        top: 32px;
        left: 24px;
      }
    }

    &__link {
      position: absolute;
      bottom: 0;
      top: 0;
      left: 0;
      right: 0;
      z-index: 2;
      @include disable-link-drag;
    }

    &--small {
      max-height: 400px;
      @include tablet {
        height: 440px;
        max-height: none;
      }

      @include big-tablet {
        height: 480px;
      }
    }

    &--middle {
      @include tablet {
        height: 378px;
      }
    }

    @include hover {
      :deep() .picture__img {
        transform: scale(1.1);
      }

      .accent-card {
        &__description {
          background-color: #292236;
        }
      }

      :deep() .card-footer {
        &__comments-count {
          color: $onDarkColor;
        }
        &__categories {
          color: $onDarkColor;
          &--category {
            @include hover {
              color: #fff;
            }
          }
        }
        &__icon {
          color: $onDarkColor;
          @include hover {
            color: #ffffff;
          }
        }
      }

      .accent-card__title {
        @include gradientTextPseudoEl(
          $gradient: radial-gradient(74.97% 74.97% at 50% 25.03%, #e8aefc 0%, #76dddc 100%)
        );
      }
    }

    &__image-wrapp {
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      z-index: 1;
      width: 100%;
      overflow: hidden;
      border-radius: 32px;
      transform: translateZ(0);

      @include big-tablet {
        max-width: 748px;
      }
    }

    &__picture {
      width: 100%;
      height: 100%;
    }

    :deep() .picture__img {
      width: 100%;
      height: 100%;
      transition: transform 0.3s linear;
      object-fit: cover;
      display: block;
      border-radius: 32px;
      overflow: hidden;
    }

    &__description {
      z-index: 3;
      display: flex;
      margin: 0 $spacing-2 $spacing-2;
      flex-direction: column;
      font-family: $text-font--1;
      background-color: #ffffff;
      padding: $spacing-4 $spacing-3 $spacing-3;
      border-radius: 28px;
      align-self: flex-end;
      width: 100%;
      max-height: 100%;

      @include tablet {
        padding: $spacing-5 $spacing-4 $spacing-4;
      }
    }
    &__title {
      margin-bottom: $spacing-4;
      font-family: $headline-font--1;
      position: relative;
      word-break: break-word;
      font-weight: 400;
      @include lineClampEllipsis(4);

      @include desktop {
        @include lineClampEllipsis(6);
      }

      @include headline-small-text;
      @include tablet {
        margin-bottom: $spacing-6;
        @include headline-medium-text;
      }
    }

    &__button {
      margin-right: $spacing-3;

      &--comments-link {
        display: flex;
        align-items: center;
      }
    }

    &__footer {
      z-index: 5;
      line-height: 20px;
      font-size: 14px;
      display: flex;
      align-items: center;
    }
  }

  .accent-card--without-image {
    overflow: hidden;
    padding: 0;

    @include hover {
      .accent-card {
        &__image-wrapp {
          background: #292236;
        }
      }
    }

    .accent-card {
      &__image-wrapp {
        background: radial-gradient(74.97% 74.97% at 50% 25.03%, #e8aefc 0%, #76dddc 100%);
      }

      &__title {
        width: 100%;
        @include headline-normal-text;
        @include lineClampEllipsis(6);

        @include tablet {
          @include headline-big-text;
          @include lineClampEllipsis(7);
        }
      }
      &__description {
        background-color: unset;
        margin: 0;
        height: 100%;
        padding: $spacing-6 $spacing-4 $spacing-4;

        @include tablet {
          padding: $spacing-8 $spacing-6 $spacing-6;
        }
      }

      &__footer {
        margin-top: auto;
      }
    }

    &.accent-card--with-secret {
      .accent-card__description {
        padding-top: 72px;

        @include tablet {
          padding-top: 84px;
        }
      }
    }
  }
  .accent-card--big {
    flex-wrap: wrap;
    overflow: initial;
    height: unset;
    padding-bottom: $spacing-7;
    // min-height: 384px;
    @include big-tablet {
      padding-bottom: $spacing-9;
    }
    @include desktop {
      padding-bottom: 0;
    }

    @include hover {
      .accent-card {
        &__image-wrapp {
          @include desktop {
            &::after {
              opacity: 1;
            }
          }
        }
        &__description {
          background-color: unset;
        }
      }
    }

    :deep() .card-footer {
      color: $onDarkColor;
      &__categories {
        color: $onDarkColor;
      }
      &__icon {
        color: $onDarkColor;
      }
    }

    .accent-card {
      &__image-wrapp {
        display: flex;
        margin-bottom: $spacing-4;
        min-height: 288px;
        position: relative;

        @include desktop {
          &::after {
            border: 4px solid #000000;
            position: absolute;
            content: '';
            border-radius: 25px;
            top: $spacing-4;
            left: $spacing-4;
            right: $spacing-4;
            bottom: $spacing-4;
            opacity: 0;
            pointer-events: none;
            transition: opacity 0.3s linear;
          }
        }

        @include tablet {
          margin-bottom: $spacing-6;
        }

        @include big-tablet {
          margin-bottom: $spacing-6;
        }

        @include desktop {
          margin-bottom: 0;
          max-height: 496px;
        }
      }

      &__description {
        background-color: black;
        color: #ffffff;
        padding: 0;

        @include desktop {
          padding: $spacing-6 0 0;
        }
      }

      &__title {
        @include headline-normal-text;

        @include tablet {
          @include font(
            (
              'fs': 32px,
              'lh': 44px,
            )
          );
        }

        @include desktop {
          width: 100%;
        }
      }

      &__footer {
        @include desktop {
          margin-top: auto;
          width: 100%;
        }
      }
    }
  }

  .accent-card--big-inside-tv {
    padding-bottom: $spacing-4;
    @include big-tablet {
      padding-bottom: $spacing-9;
    }

    @include desktop {
      padding-bottom: $spacing-0;
      flex-wrap: nowrap;
      height: 100%;
    }

    .accent-card {
      &__description {
        height: 100%;

        @include desktop {
          align-self: auto;
          padding: $spacing-6 0 $spacing-12 $spacing-6;
          max-width: 348px;
        }
      }

      &__title {
        word-break: break-word;
        @include lineClampEllipsis(8);

        @include tablet {
          @include lineClampEllipsis(9);
        }
      }
    }
  }
</style>
